import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from "@angular/core";
import { Entity } from "@app/entity/model/entity";
import { AlulaDealer, AlulaDevice, CHDevice, Project, SecuritySystem } from "@app/project/model";
import { AlulaDealerService } from "@app/project/services/alula-dealer.service";
import { AlulaReceiverService } from "@app/project/services/alula-receiver.service";
import { CHServiceService } from "@app/project/services/ch-services.service";
import { ProjectHelperService } from "@app/project/services/project-helper.service";
import { ProjectService } from "@app/project/services/project.service";
import { AlulaActions } from "@app/project/store";
import { DeviceDispatchers } from "@app/project/store/dispatchers/device.dispatchers";
import { ProjectDispatchers } from "@app/project/store/dispatchers/project.dispatchers";
import { ApiResultState, LoadingState } from "@app/root-store/api-result.state";
import { DEVICE_CLASSES } from "@app/shared/services/equator-api/ClareHomeRpc.constants";
import { Action } from "@ngrx/store";
import { filter, map, Observable, of, share, Subject, switchMap, take, takeUntil, tap } from "rxjs";

@Component({
    selector: "app-configure-monitoring-service-step",
    templateUrl: "./configure-monitoring-service-step.component.html",
    styleUrls: ["./configure-monitoring-service-step.component.scss"],
})
export class ConfigureMonitoringServiceStepComponent implements OnInit, OnDestroy {
    @Input() project: Project;
    @Input() entity: Entity;
    @Input() isProjectOnline = false;
    @Input() partitionDevice: CHDevice;
    @Input() isClareOne = false;
    @Input() ignoreMonitoringService = false;

    @Output() updateComplete = new EventEmitter();

    macAddress$: Observable<string>;
    alulaDealerId: string;
    dbSecurityDevice: SecuritySystem;
    clareOneCallerIdNumber$: Observable<string>;
    alulaDealer$: Observable<AlulaDealer>;
    receiverGroupSource$: Observable<any[] | Error>;
    alulaDevice$: Observable<ApiResultState<AlulaDevice>>;
    defaultDevice: Partial<AlulaDevice>;

    private unsubscribe$ = new Subject<void>();

    constructor(
        private projectService: ProjectService,
        private readonly chServiceService: CHServiceService,
        private deviceDispatchers: DeviceDispatchers,
        private projectDispatchers: ProjectDispatchers,
        private alulaDealerService: AlulaDealerService,
        private readonly alulaReceiverService: AlulaReceiverService
    ) {}

    ngOnInit(): void {
        this.alulaDealerId = this.entity?.alulaAccountNumber;
        this.dbSecurityDevice = ProjectHelperService.getDbSecuritySystem(this.project);

        this.clareOneCallerIdNumber$ = this.getClareOneCallerIdNumber$();
        this.alulaDealer$ = this.getAlulaDealer$().pipe(share());
        this.receiverGroupSource$ = this.getReceiverGroup$();
        this.alulaDevice$ = this.getAlulaDevice$();

        this.alulaDevice$
            .pipe(
                filter(device => device.callState === "loaded"),
                take(1)
            )
            .subscribe(device => {
                const { macAddress, ...rest } = device.result || {};
                this.defaultDevice = rest;
            });

        this.macAddress$ = this.projectService.getMacAddress(
            this.project,
            ProjectHelperService.getProjectUuid(this.project),
            this.unsubscribe$,
            this.isProjectOnline
        );
    }

    ngOnDestroy() {
        this.unsubscribe$.next(null);
        this.unsubscribe$.complete();
    }

    onFormCompleted(action: Action) {
        this.updateComplete.emit(action);
    }

    private getAlulaDevice$() {
        const uuid = ProjectHelperService.getProjectUuid(this.project);
        return of(this.isProjectOnline).pipe(
            switchMap(isProjectOnline =>
                this.projectDispatchers.getMacAddress$(
                    ProjectHelperService.getDbSecuritySystemMacAddress(this.project),
                    uuid,
                    isProjectOnline
                )
            ),
            switchMap(mac => {
                if (mac) {
                    return this.projectDispatchers.getAlulaDevice(mac, this.unsubscribe$).pipe(
                        tap(devices => {
                            if (devices?.result) {
                                // Refetch alula user in case we cleared it after panel swap
                                // (otherwise the enable-monitoring step creates a new user)
                                this.projectDispatchers.dispatch(
                                    new AlulaActions.GetAlulaUserByPartialUsername({
                                        partialUsername: devices.result.macAddress,
                                    })
                                );
                            }
                        })
                    );
                }

                return of({ result: null, callState: LoadingState.loaded });
            }),
            takeUntil(this.unsubscribe$)
        );
    }

    private getReceiverGroup$() {
        return this.alulaDealer$.pipe(switchMap(alulaDealer => this.alulaReceiverService.getAlulaReceiverGroups(alulaDealer.id)));
    }

    private getAlulaDealer$() {
        return this.projectDispatchers.getActiveProjectEntity$().pipe(
            takeUntil(this.unsubscribe$),
            filter(entity => !!entity),
            switchMap(entity => this.alulaDealerService.getAlulaDealer(entity.alulaAccountNumber))
        );
    }

    private getClareOneCallerIdNumber$() {
        const uuid = ProjectHelperService.getProjectUuid(this.project);
        return this.chServiceService.getSecurityPanel(uuid).pipe(
            map(securityDevices => securityDevices?.[0]),
            switchMap(securityDevice =>
                this.deviceDispatchers.getDeviceByUid(securityDevice?.ChildUIDs[0]).pipe(
                    takeUntil(this.unsubscribe$),
                    filter(partitionDevice => !!partitionDevice)
                )
            ),
            switchMap(partitionDevice =>
                this.deviceDispatchers
                    .selectDeviceClassObject$(
                        ProjectHelperService.getProjectUuid(this.project),
                        partitionDevice.UID,
                        DEVICE_CLASSES.INTRUSION_PARTITION_CONFIG
                    )
                    .pipe(
                        takeUntil(this.unsubscribe$),
                        map(dco => dco?.Properties?.callerIdPhoneNumber.Value)
                    )
            )
        );
    }
}
